import type { MasterFieldDef } from './masters/types';

const SKIP_TEXT_TO_MASTER = new Set([
  'iso3Code', 'phoneCode', 'isoNumeric', 'swiftCode', 'iataCode', 'postalCodePattern',
  'referral_code', 'fieldKey', 'gateway', 'ifscPrefix', 'foodCode', 'sku', 'serviceCode',
  'locale', 'ianaId', 'operatorSlug', 'restaurantSlug', 'icon', 'imageUrl', 'address',
  'phone', 'stationType', 'targetScreen', 'unit', 'startTime', 'endTime', 'start', 'end',
  'description', 'attributeCombination', 'hex', 'symbol', 'decimalSeparator', 'thousandSeparator',
  'utcOffset', 'abbreviation', 'nativeName', 'invoicePrefix', 'nationality', 'flagUrl',
  'capacity',
]);

export const MASTER_REF_BY_FIELD_KEY: Record<string, string> = {
  parentCategoryCode: 'food_categories',
  attributeCode: 'food_attributes',
  addonGroupCode: 'addon_groups',
  vehicleTypeCode: 'vehicle_types',
  defaultCurrency: 'currencies',
  secondaryCurrency: 'currencies',
  defaultLanguage: 'languages',
  timezone: 'timezones',
};

export const MASTER_REF_BY_MASTER_TYPE: Partial<Record<string, Record<string, string>>> = {
  food_attribute_values: { attributeCode: 'food_attributes' },
  ride_attribute_values: { attributeCode: 'ride_attributes' },
  food_sub_categories: { parentCategoryCode: 'food_categories' },
  grocery_sub_categories: { parentCategoryCode: 'grocery_categories' },
  fleet_branches: {},
  ride_images: { serviceCode: 'trip_types' },
  ride_categories: { serviceTypes: 'trip_types' },
  vehicle_types: { serviceTypes: 'trip_types' },
};

export const LEGACY_PARENT_META_KEY: Partial<Record<string, string>> = {
  food_sub_categories: 'parentCategoryCode',
  grocery_sub_categories: 'parentCategoryCode',
  food_attribute_values: 'attributeCode',
  ride_attribute_values: 'attributeCode',
};

export function resolveMasterRefType(field: MasterFieldDef, masterTypeKey: string): string | null {
  if (field.type === 'zone' || field.key === 'zoneCode') return null;
  if (field.type === 'master' && field.masterType) return field.masterType;
  if (SKIP_TEXT_TO_MASTER.has(field.key)) return null;

  const contextual = MASTER_REF_BY_MASTER_TYPE[masterTypeKey]?.[field.key];
  if (contextual) return contextual;

  if (MASTER_REF_BY_FIELD_KEY[field.key]) return MASTER_REF_BY_FIELD_KEY[field.key];

  return null;
}

export function enrichMetadataFields(fields: MasterFieldDef[] = [], masterTypeKey: string): MasterFieldDef[] {
  return fields.map((field) => {
    const masterType = resolveMasterRefType(field, masterTypeKey);
    if (!masterType || field.type === 'master' || field.type === 'zone') return field;
    if (['boolean', 'number', 'select', 'currency', 'percent', 'color', 'json', 'master', 'entity', 'time', 'image'].includes(field.type)) {
      return field;
    }
    return {
      ...field,
      type: 'master' as const,
      masterType,
      label: field.label.replace(/\s+Code$/i, '').replace(/\s+Slug$/i, '') || field.label,
      helperText: field.helperText || `Select from ${masterType.replace(/_/g, ' ')} list`,
    };
  });
}

export function getMasterTypeForField(field: MasterFieldDef, masterTypeKey: string): string | null {
  if (field.type === 'master' && field.masterType) return field.masterType;
  return resolveMasterRefType(field, masterTypeKey);
}

/** Match child master row to selected parent code (parentId or legacy metadata) */
export function matchesParentMaster(
  child: { parentId?: string | { _id?: string }; metadata?: Record<string, unknown> },
  parentCode: string,
  parentRow?: { _id: string },
  legacyMetaKey?: string,
): boolean {
  if (!parentCode) return true;
  const childParentId = typeof child.parentId === 'object'
    ? child.parentId?._id
    : child.parentId;
  if (parentRow?._id && childParentId) return String(childParentId) === parentRow._id;
  if (legacyMetaKey && child.metadata?.[legacyMetaKey] === parentCode) return true;
  return false;
}
